
import Vue from 'vue';
import {
  ClassroomModel,
  CourseModel,
  QuestionModel,
  UserModel,
  ListModel,
} from '@descomplica/dex-models';
import { UnifiedCommentModel } from '@/models/UnifiedCommentModel';
import Content from '../components/contents/Content.vue';
import CommentCard from '../components/comment/CommentCard.vue';
import CommentInput from '../components/comment/CommentInput.vue';
import ContentAndSolutions from '../components/ContentAndSolutions.vue';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { BaseTrackType } from '@/helpers/analytics/types';
import { ANALYTICS_LABELS } from '@/helpers/listItems';
import { FileModel } from '@/models/FileModel';
import { maxAttemptsPracticeActivity } from '@/settings/constants';
import i18n from '@/i18n';
import { getCourseCategory } from '@/helpers/category';
import { ActivityMessage } from '@descomplica/vue-components';
import { UserListActivityStatus } from '@/services/UndergradGrades';
import DueDateWarningHelper, { ActivityMessageStatus } from '@/helpers/assessments/dueDateWarning';
import { TranslateResult } from 'vue-i18n';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';

export default Vue.extend({
  components: {
    Content,
    CommentCard,
    CommentInput,
    ContentAndSolutions,
    ActivityMessage,
  },
  props: {
    question: {
      type: Object as Vue.PropType<QuestionModel>,
      required: true,
    },
    comments: {
      type: Array as Vue.PropType<UnifiedCommentModel[]>,
      default: (): any[] => [],
    },
    classroom: {
      type: Object as Vue.PropType<ClassroomModel>,
      required: true,
    },
    listStatus: {
      type: Object as Vue.PropType<UserListActivityStatus>,
      default: null,
    },
  },
  data() {
    return {
      isSending: false,
    };
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    course(): CourseModel & { progress: number } {
      return this.user.getCourse();
    },
    currentList(): ListModel {
      return this.$store.getters.listBySlug(this.$route.params.listSlug);
    },
    currentClassroom(): ClassroomModel {
      return this.$store.getters.classroomBySlug(this.$route.params.classroomSlug);
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    gradeActivitiesViaAi(): boolean {
      return this.$store.getters.gradeActivitiesViaAi;
    },
    currentUserComment(): UnifiedCommentModel | undefined {
      return (
        this.comments.find(
          (comment: UnifiedCommentModel) => comment.author && comment.author.id === this.user.id,
        ) || undefined
      );
    },
    classroomComments(): UnifiedCommentModel[] {
      if (!this.classroom) return [];
      const { studentsIds = [] } = this.classroom;
      return this.comments.filter(
        (comment) =>
          studentsIds.includes(comment.userId) &&
          (comment.author.id === this.user.id || this.isTeacher),
      );
    },
    hasCommented(): boolean {
      return !!this.currentUserComment;
    },
    remainingAttempts(): number {
      return maxAttemptsPracticeActivity - (this.currentUserComment?.attemptCount || 0);
    },
    isTeacher(): boolean {
      return this.user.isAdmin();
    },
    attemptsMessage(): String {
      const { remainingAttempts } = this;
      const textTimes = remainingAttempts === 1 ? i18n.t('time') : i18n.t('times');
      const score = this.currentUserComment?.score;

      return remainingAttempts > 0 && (score === null || score === undefined)
        ? `${i18n.t('canEditActivity', { attempt: remainingAttempts }).toString()} ${textTimes}`
        : i18n.t('canNotEditActivity').toString();
    },
    defaultTrackingProps(): BaseTrackType {
      return {
        course: this.course.name,
        discipline: this.currentClassroom.name,
        content_type: ANALYTICS_LABELS.practice,
        category: getCourseCategory(this.course),
      };
    },
  },
  mounted() {
    Analytics.trackEvent(EVENT_LABELS.contentOpened, {
      ...this.defaultTrackingProps,
      lesson_slug: this.currentList.slug,
      lesson_name: this.currentList.name,
    });

    if (this.listStatus) this.trackingActivityMessageView();
  },
  methods: {
    async submit(params: { text: string; files: FileModel[] }) {
      if (this.isSending) return;
      const { text, files } = params;
      if (text) {
        this.isSending = true;
        const isGradeActivitiesViaAi = this.gradeActivitiesViaAi;
        await this.$store
          .dispatch('CREATE_EXERCISE_COMMENT', {
            questionId: this.question.id,
            userId: this.user.id,
            body: text,
            attemptCount: 1,
            attachments: files,
            isGradeActivitiesViaAi,
          })
          .finally(() => {
            this.isSending = false;
          });

        this.trackingContentConsumed('answered');
      }
    },
    trackingContentConsumed(contentStatus: string): void {
      Analytics.trackEvent(EVENT_LABELS.contentConsumed, {
        ...this.defaultTrackingProps,
        content_status: contentStatus,
        content_id: this.question.id,
        ...this.getTrialStatusByDescomplicaCourseId,
      });
    },
    async onCommentEdit(params: { commentId: number; text: string; files: FileModel[] }) {
      if (this.isSending) return;

      const { text, files } = params;

      if (!text || this.remainingAttempts === 0) return;

      this.isSending = true;
      const isGradeActivitiesViaAi = this.gradeActivitiesViaAi;
      await this.$store
        .dispatch('UPDATE_EXERCISE_COMMENT', {
          questionId: this.question.id,
          commentId: Number(this.currentUserComment?.id),
          body: text,
          attemptCount: this.currentUserComment?.attemptCount,
          textId: this.currentUserComment?.textId,
          attachments: files,
          isGradeActivitiesViaAi,
        })
        .finally(() => {
          this.isSending = false;
        });

      this.trackingContentConsumed('edited');
    },
    updateScore(comment: UnifiedCommentModel) {
      this.$store.dispatch('UPDATE_COMMENT_SCORE', comment);
    },
    activityStatus(): ActivityMessageStatus {
      return DueDateWarningHelper.getActivityStatus(this.listStatus.status);
    },
    warningMessage(): TranslateResult {
      return DueDateWarningHelper.getPracticeWarningMessage(
        this.listStatus.status,
        this.isPastDue(),
        this.listStatus.dueDate,
      );
    },
    isPastDue(): boolean {
      const { answeredAt, dueDate, status } = this.listStatus || {};
      return DueDateWarningHelper.isPastDue(dueDate, answeredAt, status);
    },
    trackingActivityMessageView(): void {
      Analytics.trackEvent(EVENT_LABELS.activityDeadlineView, {
        course: this.$store.getters.currentCourse.name,
        category: getCourseCategory(this.$store.getters.currentCourse),
        content_type: ANALYTICS_LABELS.practice,
        lesson_slug: this.$route.params.listSlug,
        discipline_slug: this.$route.params.classroomSlug,
        activity_status: this.listStatus.status,
      });
    },
  },
});
